export type ActivityMaturity =
  | 'active'
  | 'read-only-next'
  | 'member-mutation-next'
  | 'admin-last'
  | 'command-first'

export type ActivityActionKind = 'view' | 'member' | 'admin' | 'command'

export interface ActivityAction {
  label: string
  kind: ActivityActionKind
  disabledReason?: string
}

export interface ActivitySurface {
  key: string
  route: string
  title: string
  shortTitle: string
  group: string
  maturity: ActivityMaturity
  audience: string
  description: string
  commands: string[]
  metrics: string[]
  actions: ActivityAction[]
  testingFocus: string[]
}

export const activitySurfaces: ActivitySurface[] = [
  {
    key: 'matchroom',
    route: '/matchroom',
    title: 'Matchroom',
    shortTitle: 'Matchroom',
    group: 'Partida',
    maturity: 'active',
    audience: 'Membros verificados',
    description:
      'Sala viva para lobby, times, ready check, veto, opt-ins, voz, MVP e recap.',
    commands: ['/matchroom', '/veto', '/pug', '/fila'],
    metrics: ['Runtime conectado', 'Mutacoes protegidas', 'Veto e ready no bot'],
    actions: [
      { label: 'Abrir sala ativa', kind: 'view' },
      { label: 'Marcar pronto', kind: 'member' },
      { label: 'Preparar voz', kind: 'member' }
    ],
    testingFocus: ['ready', 'map veto', 'demo/clips opt-in', 'MVP', 'voice prep']
  },
  {
    key: 'home',
    route: '/home',
    title: 'Home do servidor',
    shortTitle: 'Home',
    group: 'Inicio',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Entrada navegavel para guias, comandos, onboarding e proximas acoes.',
    commands: ['/server-guide', '/dashboard', '/commands', '/onboarding'],
    metrics: ['93 comandos', 'Canais corretos', 'Checklist de primeiro acesso'],
    actions: [
      { label: 'Ver guia', kind: 'view' },
      { label: 'Filtrar comandos', kind: 'view' },
      { label: 'Abrir Matchroom', kind: 'view' }
    ],
    testingFocus: ['navegacao', 'filtros de guia', 'atalhos para comandos']
  },
  {
    key: 'profiles',
    route: '/profiles',
    title: 'Centro de jogadores',
    shortTitle: 'Jogadores',
    group: 'Jogadores',
    maturity: 'read-only-next',
    audience: 'Membros e admins',
    description:
      'Perfis, passport, stats, comparacoes, historico e tags em uma tela unica.',
    commands: [
      '/link-steam',
      '/profile',
      '/perfil',
      '/passport',
      '/stats',
      '/compare',
      '/elo-chart',
      '/lastmatch',
      '/avaliar',
      '/remover-avaliacao',
      '/tag-player',
      '/tags',
      '/tags-hide',
      '/trust'
    ],
    metrics: ['FACEIT/GC/Premier', 'Avaliacoes', 'Historico recente'],
    actions: [
      { label: 'Comparar jogadores', kind: 'view' },
      { label: 'Editar privacidade', kind: 'member' },
      { label: 'Avaliar jogador', kind: 'member' }
    ],
    testingFocus: ['tabs', 'comparacao', 'privacidade', 'tags']
  },
  {
    key: 'leaderboards',
    route: '/leaderboards',
    title: 'Rankings',
    shortTitle: 'Rankings',
    group: 'Jogadores',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Ranking do servidor, filtros por fonte e leitura rapida de evolucao.',
    commands: ['/ranking', '/refresh-leaderboard', '/rebuild-leaderboard'],
    metrics: ['Top 10', 'Fonte de rating', 'Atualizacao do painel'],
    actions: [
      { label: 'Ordenar por rating', kind: 'view' },
      { label: 'Filtrar plataforma', kind: 'view' },
      { label: 'Atualizar painel', kind: 'admin' }
    ],
    testingFocus: ['ordenacao', 'filtro', 'estado admin bloqueado']
  },
  {
    key: 'pro-cs',
    route: '/pro-cs',
    title: 'Pro CS Hub',
    shortTitle: 'Pro CS',
    group: 'CS2 Pro',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Agenda, partidas ao vivo, resultados, times, players e patch notes.',
    commands: ['/matches', '/pro', '/eventos-pro', '/patch-notes'],
    metrics: ['Liquipedia fallback', 'BO3 live fallback', 'CSAPI publico'],
    actions: [
      { label: 'Ver ao vivo', kind: 'view' },
      { label: 'Buscar time', kind: 'view' },
      { label: 'Rodar lembretes', kind: 'admin' }
    ],
    testingFocus: ['tabs upcoming/live/results', 'busca', 'acoes admin']
  },
  {
    key: 'inventory',
    route: '/inventory',
    title: 'Inventario e mercado',
    shortTitle: 'Inventario',
    group: 'Economia',
    maturity: 'read-only-next',
    audience: 'Membros opt-in',
    description:
      'Valor de inventario, alertas de preco, carteira e loja cosmetica.',
    commands: ['/inventory', '/track', '/wallet', '/shop', '/buy'],
    metrics: ['BRL estimado', 'Alertas ativos', 'Saldo cosmetico'],
    actions: [
      { label: 'Ordenar skins', kind: 'view' },
      { label: 'Criar alerta', kind: 'member' },
      { label: 'Comprar item', kind: 'member' }
    ],
    testingFocus: ['ordenacao', 'opt-in', 'alerta', 'compra simulada']
  },
  {
    key: 'demo-review',
    route: '/demo-review',
    title: 'Demo review',
    shortTitle: 'Demos',
    group: 'Performance',
    maturity: 'command-first',
    audience: 'Membros com arquivo .dem',
    description:
      'Historico e leitura visual de relatorios .dem; upload continua no comando.',
    commands: ['/demo analisar'],
    metrics: ['Relatorios HTML', 'Fixture real', 'Upload command-first'],
    actions: [
      { label: 'Ver relatorios', kind: 'view' },
      {
        label: 'Enviar .dem',
        kind: 'command',
        disabledReason: 'Uploads continuam no comando /demo analisar.'
      }
    ],
    testingFocus: ['lista de relatorios', 'estado command-first']
  },
  {
    key: 'matchmaking',
    route: '/matchmaking',
    title: 'Matchmaking',
    shortTitle: 'Jogar',
    group: 'Partida',
    maturity: 'member-mutation-next',
    audience: 'Membros verificados',
    description:
      'Disponibilidade, fila, PUG, lobby, party suggestions e busca de time.',
    commands: [
      '/jogar-hoje',
      '/quem-joga',
      '/indisponivel',
      '/fila',
      '/pug',
      '/lobby',
      '/entrar-lobby',
      '/procurar-time',
      '/party-suggestions'
    ],
    metrics: ['Fila casual', 'Disponiveis hoje', 'Lobbies temporarios'],
    actions: [
      { label: 'Entrar na fila', kind: 'member' },
      { label: 'Marcar horario', kind: 'member' },
      { label: 'Ver sugestoes', kind: 'view' }
    ],
    testingFocus: ['join/leave', 'filtros', 'estado vazio']
  },
  {
    key: 'teams',
    route: '/teams',
    title: 'Times',
    shortTitle: 'Times',
    group: 'Competitivo',
    maturity: 'member-mutation-next',
    audience: 'Captains e membros',
    description:
      'Team pages, convites, rosters, capitao, transferencias e dissolucao.',
    commands: ['/team', '/procurar-time'],
    metrics: ['Rosters', 'Convites pendentes', 'Captains'],
    actions: [
      { label: 'Criar time', kind: 'member' },
      { label: 'Convidar membro', kind: 'member' },
      { label: 'Transferir capitao', kind: 'member' }
    ],
    testingFocus: ['forms', 'convites', 'confirmacoes']
  },
  {
    key: 'scrims',
    route: '/scrims',
    title: 'Scrims e treino',
    shortTitle: 'Scrims',
    group: 'Competitivo',
    maturity: 'member-mutation-next',
    audience: 'Times e coaches',
    description:
      'Agenda de treino, busca de adversario, coaching e revisao organizada.',
    commands: ['/scrim', '/coach'],
    metrics: ['Scrims abertas', 'Fila de coaching', 'Areas de treino'],
    actions: [
      { label: 'Criar scrim', kind: 'member' },
      { label: 'Assumir coaching', kind: 'member' },
      { label: 'Fechar pedido', kind: 'member' }
    ],
    testingFocus: ['status de pedidos', 'filtros', 'confirmacoes']
  },
  {
    key: 'tournaments',
    route: '/tournaments',
    title: 'Torneios e partidas',
    shortTitle: 'Torneios',
    group: 'Competitivo',
    maturity: 'member-mutation-next',
    audience: 'Times e admins',
    description:
      'Torneios internos, bracket, check-in, report e confirmacao de placar.',
    commands: ['/tournament', '/match'],
    metrics: ['Bracket', 'Check-ins', 'Placares pendentes'],
    actions: [
      { label: 'Ver bracket', kind: 'view' },
      { label: 'Check-in', kind: 'member' },
      { label: 'Iniciar torneio', kind: 'admin' }
    ],
    testingFocus: ['bracket tabs', 'check-in', 'admin gated']
  },
  {
    key: 'map-veto',
    route: '/map-veto',
    title: 'Map veto',
    shortTitle: 'Veto',
    group: 'Partida',
    maturity: 'active',
    audience: 'Participantes da sala',
    description:
      'Veto visual reutilizavel para PUG, scrim, torneio e Matchroom.',
    commands: ['/veto', '/matchroom'],
    metrics: ['Picks', 'Bans', 'Map pool'],
    actions: [
      { label: 'Iniciar veto', kind: 'member' },
      { label: 'Banir mapa', kind: 'member' },
      { label: 'Cancelar veto', kind: 'member' }
    ],
    testingFocus: ['ciclo de mapa', 'bloqueios', 'estado final']
  },
  {
    key: 'pickems',
    route: '/pickems',
    title: 'Pickems',
    shortTitle: 'Pickems',
    group: 'Comunidade',
    maturity: 'member-mutation-next',
    audience: 'Todos os membros',
    description:
      'Palpites, votos, ranking de acertos e encerramento de votacoes.',
    commands: ['/pickem'],
    metrics: ['Abertos', 'Votos', 'Ranking'],
    actions: [
      { label: 'Votar A', kind: 'member' },
      { label: 'Votar B', kind: 'member' },
      { label: 'Fechar pickem', kind: 'admin' }
    ],
    testingFocus: ['voto', 'historico', 'admin gated']
  },
  {
    key: 'clips',
    route: '/clips',
    title: 'Clips',
    shortTitle: 'Clips',
    group: 'Comunidade',
    maturity: 'member-mutation-next',
    audience: 'Todos os membros',
    description:
      'Clip of the Week com envio, voto, vencedor e historico.',
    commands: ['/clip'],
    metrics: ['Clips da semana', 'Votos', 'Vencedores'],
    actions: [
      { label: 'Enviar clip', kind: 'member' },
      { label: 'Votar clip', kind: 'member' },
      { label: 'Fechar semana', kind: 'admin' }
    ],
    testingFocus: ['envio URL', 'voto', 'historico']
  },
  {
    key: 'coaching',
    route: '/coaching',
    title: 'Coaching',
    shortTitle: 'Coach',
    group: 'Performance',
    maturity: 'member-mutation-next',
    audience: 'Coaches e alunos',
    description:
      'Perfis de coach, fila de pedidos, assumir pedido e fechar sessao.',
    commands: ['/coach'],
    metrics: ['Coaches ativos', 'Pedidos abertos', 'Areas'],
    actions: [
      { label: 'Ativar perfil', kind: 'member' },
      { label: 'Pedir review', kind: 'member' },
      { label: 'Assumir pedido', kind: 'member' }
    ],
    testingFocus: ['perfil', 'fila', 'status']
  },
  {
    key: 'seasons',
    route: '/seasons',
    title: 'Temporadas',
    shortTitle: 'Season',
    group: 'Comunidade',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Temporada mensal, ranking, eventos, premios, awards e revista.',
    commands: ['/season', '/awards', '/magazine'],
    metrics: ['Pontos', 'Eventos', 'Awards'],
    actions: [
      { label: 'Check-in da season', kind: 'member' },
      { label: 'Ver awards', kind: 'view' },
      { label: 'Publicar revista', kind: 'admin' }
    ],
    testingFocus: ['ranking', 'eventos', 'admin gated']
  },
  {
    key: 'hall-of-fame',
    route: '/hall-of-fame',
    title: 'Hall of Fame',
    shortTitle: 'HOF',
    group: 'Comunidade',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Memoria permanente de clips, temporadas, rivalidades e marcos.',
    commands: ['/hof', '/rivals', '/duelo'],
    metrics: ['Marcos', 'Clips eternizados', 'Rivalidades saudaveis'],
    actions: [
      { label: 'Filtrar categoria', kind: 'view' },
      { label: 'Sugerir duelo', kind: 'member' },
      { label: 'Adicionar marco', kind: 'admin' }
    ],
    testingFocus: ['filtros', 'lista', 'admin gated']
  },
  {
    key: 'economy',
    route: '/economy',
    title: 'Economia',
    shortTitle: 'Economia',
    group: 'Economia',
    maturity: 'member-mutation-next',
    audience: 'Membros e admins',
    description:
      'Carteira, loja, compras, pontos auditaveis e historico de economia.',
    commands: ['/wallet', '/shop', '/buy', '/points'],
    metrics: ['Saldo', 'Itens', 'Historico auditavel'],
    actions: [
      { label: 'Ver loja', kind: 'view' },
      { label: 'Comprar cosmetico', kind: 'member' },
      { label: 'Ajustar pontos', kind: 'admin' }
    ],
    testingFocus: ['compra', 'saldo', 'admin gated']
  },
  {
    key: 'reports',
    route: '/reports',
    title: 'Reports',
    shortTitle: 'Reports',
    group: 'Admin',
    maturity: 'read-only-next',
    audience: 'Admins',
    description:
      'Reports deterministas, HTML anexado, analytics, comandos e updates.',
    commands: ['/reports', '/commands', '/agent-update'],
    metrics: ['HTML self-contained', 'Embeds compactos', 'Sem IDs crus'],
    actions: [
      { label: 'Gerar report pack', kind: 'admin' },
      { label: 'Ver inventario', kind: 'view' },
      { label: 'Template update', kind: 'admin' }
    ],
    testingFocus: ['tabs', 'download/preview', 'admin gated']
  },
  {
    key: 'roadmap',
    route: '/roadmap',
    title: 'Roadmap',
    shortTitle: 'Roadmap',
    group: 'Admin',
    maturity: 'read-only-next',
    audience: 'Admins e owners',
    description:
      'Status dos RMs, evidencias, bloqueios, proximos passos e historico.',
    commands: ['/roadmap', '/agent-update'],
    metrics: ['RMs completos', 'Ready-for-live-proof', 'Bloqueios'],
    actions: [
      { label: 'Filtrar status', kind: 'view' },
      { label: 'Abrir evidencia', kind: 'view' },
      { label: 'Publicar update', kind: 'admin' }
    ],
    testingFocus: ['filtros', 'detalhes', 'admin gated']
  },
  {
    key: 'server-guide',
    route: '/server-guide',
    title: 'Guia do servidor',
    shortTitle: 'Guia',
    group: 'Inicio',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Canais, regras, roles, onboarding, comandos certos e como participar.',
    commands: ['/server-guide', '/channels', '/badges', '/brand'],
    metrics: ['Canais corretos', 'Roles visuais', 'Brand assets'],
    actions: [
      { label: 'Ver canais', kind: 'view' },
      { label: 'Ver badges', kind: 'view' },
      { label: 'Atualizar guia', kind: 'admin' }
    ],
    testingFocus: ['navegacao', 'canais', 'admin gated']
  },
  {
    key: 'brand',
    route: '/brand',
    title: 'Marca e assets',
    shortTitle: 'Marca',
    group: 'Admin',
    maturity: 'read-only-next',
    audience: 'Admins e criadores',
    description:
      'Logo oficial, cores, icons, role icons, assets e status de identidade.',
    commands: ['/brand', '/badges'],
    metrics: ['Logo oficial', 'Tokens', 'Role icons'],
    actions: [
      { label: 'Ver guia visual', kind: 'view' },
      { label: 'Ver assets', kind: 'view' },
      { label: 'Auditar badges', kind: 'admin' }
    ],
    testingFocus: ['asset tabs', 'status', 'admin gated']
  },
  {
    key: 'source-servers',
    route: '/source-servers',
    title: 'Servidores CS',
    shortTitle: 'Servers',
    group: 'Partida',
    maturity: 'read-only-next',
    audience: 'Membros e admins',
    description:
      'Browser visual dos servidores salvos, query de status e operacao de conexao.',
    commands: ['/server'],
    metrics: ['Servidores salvos', 'Status query', 'Sem IP privado cru'],
    actions: [
      { label: 'Ver servidores', kind: 'view' },
      { label: 'Consultar status', kind: 'view' },
      { label: 'Salvar servidor', kind: 'admin' }
    ],
    testingFocus: ['lista', 'query', 'admin gated']
  },
  {
    key: 'twitch',
    route: '/twitch',
    title: 'Twitch',
    shortTitle: 'Twitch',
    group: 'Comunidade',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Painel de canais ao vivo, status do provider e alertas de stream do servidor.',
    commands: ['/twitch'],
    metrics: ['Provider Twitch', 'Canais monitorados', 'Alertas live'],
    actions: [
      { label: 'Ver canais live', kind: 'view' },
      { label: 'Filtrar jogo', kind: 'view' },
      { label: 'Forcar sync', kind: 'admin' }
    ],
    testingFocus: ['lista live', 'provider fallback', 'admin gated']
  },
  {
    key: 'soundboard',
    route: '/soundboard',
    title: 'Soundboard',
    shortTitle: 'Sons',
    group: 'Comunidade',
    maturity: 'read-only-next',
    audience: 'Todos os membros',
    description:
      'Inventario dos sons oficiais, regras de uso, permissoes e momentos recomendados.',
    commands: ['/soundboard'],
    metrics: ['Pack oficial', 'Regras moderadas', 'Permissoes por role'],
    actions: [
      { label: 'Ver pack', kind: 'view' },
      { label: 'Ver permissoes', kind: 'view' },
      { label: 'Auditar sons', kind: 'admin' }
    ],
    testingFocus: ['pack', 'rules', 'admin gated']
  },
  {
    key: 'admin',
    route: '/admin',
    title: 'Operacoes admin',
    shortTitle: 'Admin',
    group: 'Admin',
    maturity: 'admin-last',
    audience: 'Admins',
    description:
      'Cron, DB, backups, probes, role sync, reset, unlink e operacoes sensiveis.',
    commands: [
      '/cron-status',
      '/db-stats',
      '/backup',
      '/refresh-roles',
      '/force-sync',
      '/reset-user',
      '/unlink',
      '/probe-faceit',
      '/probe-gc',
      '/probe-premier',
      '/cleanup-left',
      '/admin-info',
      '/levelup-channel',
      '/smurf-threshold',
      '/trust'
    ],
    metrics: ['Confirmacoes obrigatorias', 'Auditoria', 'Sem dados privados crus'],
    actions: [
      { label: 'Ver saude', kind: 'view' },
      { label: 'Rodar probe', kind: 'admin' },
      {
        label: 'Resetar membro',
        kind: 'admin',
        disabledReason: 'Acoes destrutivas exigem confirmacao auditada no backend.'
      }
    ],
    testingFocus: ['confirmacoes', 'permissoes', 'estado bloqueado']
  }
]

export const defaultSurfaceKey = 'home'

export function surfaceByKey(key: string | null | undefined): ActivitySurface {
  return (
    activitySurfaces.find((surface) => surface.key === key) ??
    activitySurfaces.find((surface) => surface.key === defaultSurfaceKey) ??
    activitySurfaces[0]
  )
}

export function surfaceByRoute(pathname: string): ActivitySurface {
  const defaultRoute = surfaceByKey(defaultSurfaceKey).route
  const normalized = pathname === '/' ? defaultRoute : pathname.replace(/\/+$/, '')
  return (
    activitySurfaces.find((surface) => surface.route === normalized) ??
    surfaceByKey(defaultSurfaceKey)
  )
}

export function groupedSurfaces(): Array<{ group: string; surfaces: ActivitySurface[] }> {
  const groups = new Map<string, ActivitySurface[]>()
  for (const surface of activitySurfaces) {
    groups.set(surface.group, [...(groups.get(surface.group) ?? []), surface])
  }
  return [...groups.entries()].map(([group, surfaces]) => ({ group, surfaces }))
}
